import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API_BASE, wsUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import {
  ArrowLeft, Download, Trash2, UploadCloud, User, Phone, Mail, CreditCard,
  MapPin, FileText, Eye, X, Send, MessageSquare, FileInput, Paperclip,
  Smile, Share2, MoreVertical, CheckCircle2, ShieldCheck,
} from "lucide-react";

const STATUSES = ["New Case", "Document Pending", "Work in Process", "Sent for Approval", "Approved", "Completed", "Not Approved", "On Hold", "Chalan Pending", "Cancel"];
const PRIORITIES = ["Low", "Medium", "High", "Urgent"];
const SERVICES = ["ITR", "GST Registration", "GST Return Filing", "TDS Return Filing", "ROC Compliance", "Private Limited Company Registration", "Audit", "CA Certification", "Other Services"];

const slaBadge = (s) => ({ "On Time": "badge-green", "Due Soon": "badge-yellow", "Overdue": "badge-red" }[s] || "badge-gray");

// ---- helpers ----
const fmtBytes = (b) => {
  if (b == null) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};
const fmtDocDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return `${d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}, ${d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })}`;
};
const dateOnly = (v) => v ? new Date(v).toLocaleDateString("en-GB") : "";
const fmtMsgTS = (v) => {
  if (!v) return "";
  const d = new Date(v);
  const date = d.toLocaleDateString("en-GB");
  const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }).toLowerCase();
  return `${date} ${time}`;
};

// Small palette to color-code sender avatars deterministically by name
const AVATAR_COLORS = [
  "bg-violet-500", "bg-indigo-500", "bg-sky-500", "bg-emerald-500",
  "bg-amber-500", "bg-rose-500", "bg-cyan-500", "bg-fuchsia-500",
];
const hashColor = (s) => {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = (h * 31 + (s || "").charCodeAt(i)) & 0xffff;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
const initials = (name) => (name || "?").split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
const roleTag = (role, service_type) => {
  if (role === "connector") return "CON";
  if (role === "employee") return service_type || "EMP";
  if (role === "admin") return "ADM";
  return "";
};

// Document thumbnail — fetches blob for images, shows PDF/generic icon otherwise
function DocThumb({ doc, onClick }) {
  const [src, setSrc] = useState("");
  const ext = (doc.filename || "").split(".").pop().toLowerCase();
  const isImage = ["png", "jpg", "jpeg", "gif", "webp", "bmp"].includes(ext);
  useEffect(() => {
    let revoked = false, blobUrl = "";
    if (!isImage) return () => {};
    (async () => {
      try {
        const token = localStorage.getItem("crm_token");
        const res = await fetch(`${API_BASE}${doc.url.replace("/api", "")}`, {
          headers: { Authorization: `Bearer ${token}` }, credentials: "include",
        });
        const blob = await res.blob();
        blobUrl = URL.createObjectURL(blob);
        if (!revoked) setSrc(blobUrl);
      } catch { /* silent */ }
    })();
    return () => { revoked = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.id]);
  return (
    <div
      className="h-32 w-full rounded-lg bg-slate-50 dark:bg-slate-800 border border-border/60 overflow-hidden flex items-center justify-center cursor-pointer hover:ring-2 hover:ring-primary/30 transition"
      onClick={onClick}
      title="Click to preview"
    >
      {src ? (
        <img src={src} alt={doc.filename} className="object-cover w-full h-full" />
      ) : ext === "pdf" ? (
        <div className="text-center text-red-500">
          <FileText size={32} className="mx-auto" />
          <div className="text-[10px] font-bold mt-1">PDF</div>
        </div>
      ) : (
        <FileText size={30} className="text-muted-foreground" />
      )}
    </div>
  );
}

// Inline editable field for the compact left column
function CompactField({ icon: Icon, label, value, canEdit, onSave, type = "text", multiline }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value ?? "");
  useEffect(() => { setV(value ?? ""); }, [value]);
  return (
    <div className="py-2.5 border-b border-border/40 last:border-0">
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
        {Icon && <Icon size={11} />} {label}
      </div>
      {editing && canEdit ? (
        <div className="flex gap-1 mt-1">
          {multiline ? (
            <textarea className="input !py-1 !text-sm flex-1 min-h-[54px]" value={v} onChange={(e) => setV(e.target.value)} autoFocus />
          ) : (
            <input className="input !py-1 !text-sm flex-1" type={type} value={v} onChange={(e) => setV(e.target.value)} autoFocus />
          )}
          <button className="btn btn-primary !py-1 !px-2 !text-xs" onClick={async () => { await onSave(v === "" ? null : v); setEditing(false); }}>Save</button>
          <button className="btn btn-outline !py-1 !px-2 !text-xs" onClick={() => { setV(value ?? ""); setEditing(false); }}><X size={12} /></button>
        </div>
      ) : (
        <div
          className={`text-sm font-medium mt-1 break-words ${canEdit ? "cursor-pointer hover:bg-primary/5 rounded -mx-1 px-1" : ""}`}
          onClick={() => canEdit && setEditing(true)}
          title={canEdit ? "Click to edit" : ""}
        >
          {value || <span className="text-muted-foreground">—</span>}
        </div>
      )}
    </div>
  );
}

export default function LeadDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const perms = user?.permissions || [];
  const canFullEdit = isAdmin || (user?.role === "employee" && perms.includes("edit_leads"));
  const canChangeStatus = isAdmin || (user?.role === "employee" && (perms.includes("change_status") || (user?.allowed_statuses || []).length > 0));
  const canAssign = isAdmin || (user?.role === "employee" && perms.includes("assign_leads"));
  const canEdit = canFullEdit || canChangeStatus || canAssign;

  const [lead, setLead] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [uploadCat, setUploadCat] = useState("Aadhar Card");
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState(null);

  // Chat state
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const wsRef = useRef(null);
  const scrollRef = useRef(null);

  const load = async () => {
    const r = await api.get(`/leads/${id}`);
    setLead(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    if (canAssign) api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  }, [canAssign]);

  // Load messages + open WebSocket on mount (chat is always visible in this layout)
  useEffect(() => {
    if (!lead) return;
    (async () => {
      const r = await api.get(`/leads/${lead.id}/messages`);
      setMessages(r.data);
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 100);
    })();
    const ws = new WebSocket(wsUrl(`lead:${lead.id}`));
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      try {
        const p = JSON.parse(ev.data);
        if (p.event === "message") {
          setMessages((m) => [...m, p.data]);
          setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 40);
        }
      } catch { /* ignore */ }
    };
    return () => { try { ws.close(); } catch { /* ignore */ } };
    // eslint-disable-next-line
  }, [lead?.id]);

  const updateField = async (field, value) => {
    try {
      await api.patch(`/leads/${id}`, { [field]: value });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Update failed");
    }
  };

  const upload = async (cat, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("category", cat);
      fd.append("file", file);
      await api.post(`/leads/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (err) { alert(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const fetchDocBlob = async (doc) => {
    const token = localStorage.getItem("crm_token");
    const res = await fetch(`${API_BASE}${doc.url.replace("/api", "")}`, {
      headers: { Authorization: `Bearer ${token}` }, credentials: "include",
    });
    return await res.blob();
  };

  const openViewer = async (doc) => {
    try {
      const blob = await fetchDocBlob(doc);
      setViewer({ url: URL.createObjectURL(blob), filename: doc.filename, mime: blob.type, doc });
    } catch { alert("Failed to load preview"); }
  };
  const closeViewer = () => { if (viewer?.url) URL.revokeObjectURL(viewer.url); setViewer(null); };

  const download = async (doc) => {
    const blob = await fetchDocBlob(doc);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = doc.filename; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAllZip = async () => {
    try {
      const token = localStorage.getItem("crm_token");
      const res = await fetch(`${API_BASE}/leads/${id}/documents/zip`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: "include",
      });
      if (!res.ok) throw new Error("zip failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lead_${lead.lead_number}_documents.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { alert("Failed to download all documents"); }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/leads/${id}/documents/${docId}`);
    load();
  };

  const deleteLead = async () => {
    if (!window.confirm(`Delete #${lead.lead_number} — ${lead.client_name}?\nThis will remove all uploaded documents. This cannot be undone.`)) return;
    try { await api.delete(`/leads/${id}`); navigate("/leads"); }
    catch (err) { alert(err?.response?.data?.detail || "Failed to delete lead"); }
  };

  const sendMsg = async (e) => {
    e?.preventDefault?.();
    if (!text.trim()) return;
    const body = text.trim();
    setText("");
    await api.post(`/leads/${id}/messages`, { text: body, kind: "text" });
  };

  const isConnector = user?.role === "connector";
  const isOwnerConnector = isConnector && lead?.connector_id === user?.id;
  const isSentForApproval = lead?.status === "Sent for Approval";
  const showApprovalActions = isOwnerConnector && isSentForApproval;

  const runApproval = async (decision) => {
    const note = window.prompt(`Add a note for ${decision === "approve" ? "APPROVED" : "NOT APPROVED"} (optional):`) || "";
    try {
      await api.post(`/leads/${id}/${decision}`, { note });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Action failed");
    }
  };

  // Employee's allowed statuses filter — empty list = block ALL (spec Phase 3)
  const allowedStatuses = user?.allowed_statuses;
  const employeeStatusOptions = user?.role === "employee"
    ? STATUSES.filter((s) => (allowedStatuses || []).includes(s))
    : STATUSES;
  const currentStatusList = user?.role === "employee" ? employeeStatusOptions : STATUSES;
  // Hide status dropdown entirely for employees with no allowed statuses
  const employeeCanChangeAnyStatus = user?.role !== "employee" || currentStatusList.length > 0;

  if (!lead) return <div className="p-6 text-center text-muted-foreground">Loading…</div>;

  // Build grouped chat entries: [{date}, ...messages of that date, {date}, ...]
  const grouped = [];
  let lastDate = "";
  (messages || []).forEach((m) => {
    const d = dateOnly(m.timestamp);
    if (d !== lastDate) {
      grouped.push({ __sep: true, date: d });
      lastDate = d;
    }
    grouped.push(m);
  });

  return (
    <div className="space-y-4" data-testid="lead-detail-page">
      {/* Back link */}
      <button onClick={() => navigate(-1)} className="link-blue text-sm inline-flex items-center gap-1" data-testid="btn-back-leads"><ArrowLeft size={16} /> Back to leads</button>

      {/* Header: Lead number only */}
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-slate-800 dark:text-slate-100" data-testid="lead-header-name">
          Lead <span className="text-primary">#{lead.lead_number}</span>
        </h1>
        <div className="text-xs text-muted-foreground mt-1">
          Created {new Date(lead.created_at).toLocaleDateString()} · Deadline {lead.deadline_at ? new Date(lead.deadline_at).toLocaleDateString() : "—"}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <StatusBadge status={lead.status} />
          {lead.sla_status && lead.sla_status !== "N/A" && <span className={`badge ${slaBadge(lead.sla_status)}`}>{lead.sla_status}</span>}
        </div>
      </div>

      {/* Two-column body: Left (main content) + Right (sticky chat) */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
        <div className="xl:col-span-3 space-y-4">
          {/* Lead Actions section — TOP */}
          <div className="rounded-xl bg-white dark:bg-slate-900/60 border border-border/60 overflow-hidden">
            <div className="px-4 py-3 flex items-center gap-2 text-white font-bold" style={{ background: "linear-gradient(90deg, #2563eb, #3b82f6)" }}>
              <ShieldCheck size={18} /> Lead Actions
            </div>
            <div className="p-4 flex flex-wrap items-center gap-3">
              {showApprovalActions && (
                <>
                  <button onClick={() => runApproval("approve")} className="btn !py-2 !px-4 !text-sm text-white font-bold rounded-full" style={{ backgroundColor: "#16A34A" }} data-testid="btn-connector-approve">✓ APPROVED</button>
                  <button onClick={() => runApproval("reject")} className="btn !py-2 !px-4 !text-sm text-white font-bold rounded-full" style={{ backgroundColor: "#DC2626" }} data-testid="btn-connector-reject">✕ NOT APPROVED</button>
                </>
              )}
              {canChangeStatus && employeeCanChangeAnyStatus && (
                <select className="input !py-2 !text-sm !w-auto rounded-full" value={lead.status} onChange={(e) => updateField("status", e.target.value)} data-testid="lead-status-select">
                  {currentStatusList.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              )}
              {canFullEdit && (
                <select className="input !py-2 !text-sm !w-auto rounded-full" value={lead.priority} onChange={(e) => updateField("priority", e.target.value)} data-testid="lead-priority-select">
                  {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              )}
              {isAdmin && (
                <select className="input !py-2 !text-sm !w-auto flex-1 min-w-[200px] rounded-full ring-2 ring-primary/30 border-primary" value={lead.service_type || "Other"} onChange={(e) => updateField("service_type", e.target.value)} data-testid="lead-service-select">
                  {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              )}
              {canAssign && (
                <select className="input !py-2 !text-sm !w-auto rounded-full" value={lead.assigned_to_id || ""} onChange={(e) => updateField("assigned_to_id", e.target.value)} data-testid="lead-assignee-select">
                  <option value="">Unassigned</option>
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              )}
              {(isAdmin || (user?.role === "employee" && perms.includes("delete_leads"))) && (
                <button onClick={deleteLead} className="btn !py-2 !px-4 !text-sm font-bold text-white rounded-full inline-flex items-center gap-2" style={{ backgroundColor: "#ef4444" }} data-testid="btn-delete-lead"><Trash2 size={14} /> Delete</button>
              )}
            </div>
          </div>

          {/* Connector Details card */}
          <div className="rounded-xl border-2 border-primary/20 bg-white dark:bg-slate-900/60 p-4 shadow-sm flex items-start gap-4" data-testid="connector-card">
            <div className={`w-11 h-11 rounded-xl flex-shrink-0 flex items-center justify-center text-white font-bold ${hashColor(lead.connector_name)}`}>
              {initials(lead.connector_name)}
            </div>
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-3 text-[11px] font-bold uppercase tracking-wider text-primary -mb-1">CONNECTOR DETAILS</div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><User size={11} /> Connector Name</div>
                <div className="font-bold text-slate-800 dark:text-slate-100 leading-tight mt-1">{lead.connector_name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">ID: {lead.connector_username}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Phone size={11} /> Phone</div>
                {lead.connector_phone ? (
                  <a href={`tel:${lead.connector_phone}`} className="link-blue font-semibold mt-1 block" data-testid="connector-phone-link">{lead.connector_phone}</a>
                ) : <div className="text-sm text-muted-foreground mt-1">—</div>}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Mail size={11} /> Email</div>
                {lead.connector_email ? (
                  <a href={`mailto:${lead.connector_email}`} className="link-blue font-semibold break-all mt-1 block" data-testid="connector-email-link">{lead.connector_email}</a>
                ) : <div className="text-sm text-muted-foreground mt-1">—</div>}
              </div>
            </div>
          </div>

          {/* Client Information section */}
          <div className="rounded-xl bg-white dark:bg-slate-900/60 border border-border/60 overflow-hidden" data-testid="client-info">
            <div className="px-4 py-3 flex items-center gap-2 text-white font-bold" style={{ background: "linear-gradient(90deg, #2563eb, #3b82f6)" }}>
              <User size={18} /> Client Information
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left: Avatar + PAN/GSTIN/Aadhar */}
              <div className="flex flex-col items-center text-center gap-3">
                <div className="w-24 h-24 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400">
                  <User size={48} />
                </div>
                <div className="text-sm space-y-1.5">
                  <div><span className="font-bold text-slate-800 dark:text-slate-100">PAN:</span> <span className="text-slate-600 dark:text-slate-300">{lead.pan || "—"}</span></div>
                  <div><span className="font-bold text-slate-800 dark:text-slate-100">GSTIN:</span> <span className="text-slate-600 dark:text-slate-300">{lead.gst_number || "Not Available"}</span></div>
                  <div className="flex items-center justify-center gap-1"><span className="font-bold text-slate-800 dark:text-slate-100">Aadhar Linked:</span> <span className={`px-2 py-0.5 rounded-full text-xs font-semibold text-white ${lead.aadhaar ? "bg-green-500" : "bg-slate-400"}`}>{lead.aadhaar ? "Yes" : "No"}</span></div>
                </div>
              </div>
              {/* Right: Client Name, Mobile, Email, Address, Details of Client */}
              <div className="space-y-2">
                <CompactField icon={User} label="Client Name" value={lead.client_name} canEdit={canFullEdit} onSave={(v) => updateField("client_name", v)} />
                <CompactField icon={Phone} label="Mobile" value={lead.phone} canEdit={canFullEdit} onSave={(v) => updateField("phone", v)} />
                <CompactField icon={Mail} label="Personal Email Id" value={lead.email} canEdit={canFullEdit} onSave={(v) => updateField("email", v)} type="email" />
                <CompactField icon={MapPin} label="Address" value={lead.address} canEdit={canFullEdit} onSave={(v) => updateField("address", v)} multiline />
                <div className="pt-2 border-t border-border/40">
                  <CompactField icon={FileText} label="Details of Client" value={lead.remarks} canEdit={canFullEdit} onSave={(v) => updateField("remarks", v)} multiline />
                </div>
              </div>
            </div>
          </div>

          {/* Documents section */}
          <div className="rounded-xl bg-white dark:bg-slate-900/60 border border-border/60 overflow-hidden" data-testid="documents-column">
            <div className="px-4 py-3 flex items-center gap-2 text-white font-bold" style={{ background: "linear-gradient(90deg, #2563eb, #3b82f6)" }}>
              <FileText size={18} /> Documents ({(lead.documents || []).length})
            </div>
            <div className="p-4 space-y-3">
              {(lead.documents || []).length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-8">No documents uploaded yet.</div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                  {lead.documents.map((d) => (
                    <div key={d.id} className="rounded-xl border border-border/60 p-3 bg-white dark:bg-slate-900/40 hover:shadow-md transition text-center relative group" data-testid={`doc-card-${d.id}`}>
                      <DocThumb doc={d} onClick={() => openViewer(d)} />
                      <div className="mt-2 text-xs font-semibold truncate" title={d.filename}>{d.filename}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">{fmtDocDate(d.uploaded_at)}</div>
                      <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                        <button className="btn btn-ghost !p-1.5 bg-white/90 shadow" onClick={() => download(d)} title="Download" data-testid={`download-${d.id}`}><Download size={13} className="text-primary" /></button>
                        {(isAdmin || (user?.role === "employee" && perms.includes("manage_documents"))) && (
                          <button className="btn btn-ghost !p-1.5 bg-white/90 shadow text-red-500" onClick={() => deleteDoc(d.id)} title="Delete" data-testid={`delete-${d.id}`}><Trash2 size={13} /></button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload + Zip download buttons row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                <label className={`btn cursor-pointer !py-3 !text-sm font-bold text-white justify-center rounded-xl ${uploading ? "opacity-60 pointer-events-none" : ""}`} style={{ backgroundColor: "#16A34A" }} data-testid="upload-doc-btn">
                  <UploadCloud size={16} />
                  {uploading ? "Uploading…" : "Upload Document"}
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.csv,.zip,image/*,application/pdf"
                    className="hidden"
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      e.target.value = "";
                      for (const f of files) {
                        // Auto-detect category from file type; default Other
                        let cat = "Other";
                        const ext = (f.name.split(".").pop() || "").toLowerCase();
                        if (["jpg","jpeg","png","webp"].includes(ext)) cat = "Passport Photo";
                        else if (ext === "pdf") cat = "Other";
                        await upload(cat, f);
                      }
                    }}
                    data-testid="upload-doc-input"
                  />
                </label>
                <button
                  onClick={downloadAllZip}
                  disabled={(lead.documents || []).length === 0}
                  className="btn !py-3 !text-sm font-bold text-white justify-center rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: "#475569" }}
                  data-testid="download-zip-btn"
                >
                  <Download size={16} /> Download all Documents as Zip
                </button>
              </div>
            </div>
          </div>

        </div>

        {/* Right sticky chat panel */}
        <div className="xl:col-span-1">
          <div className="rounded-xl bg-white dark:bg-slate-900/60 border border-border/60 overflow-hidden flex flex-col sticky top-4" style={{ height: "640px" }} data-testid="chat-column">
            <div className="px-4 py-3 flex items-center justify-between text-white" style={{ background: "linear-gradient(90deg, #2563eb, #3b82f6)" }}>
              <div>
                <div className="font-extrabold text-lg flex items-center gap-2"><MessageSquare size={18} /> Chat</div>
                <div className="text-[11px] flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" /> Online</div>
              </div>
              <button className="btn btn-ghost !p-1 text-white" title="More"><MoreVertical size={16} /></button>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-scroll px-3 py-3 space-y-3 chat-scroll" style={{ scrollBehavior: "smooth" }} data-testid="chat-body">
              {messages.length === 0 && <div className="text-center text-sm text-muted-foreground py-8">No messages yet.</div>}
              {grouped.map((item, i) => {
                if (item.__sep) {
                  return (
                    <div key={`sep-${i}`} className="flex justify-center">
                      <span className="px-3 py-1 rounded-full text-[11px] font-bold" style={{ backgroundColor: "#fef3c7", color: "#92400e" }}>{item.date}</span>
                    </div>
                  );
                }
                const m = item;
                const tag = roleTag(m.from_user_role, lead.service_type);
                const displayName = tag ? `(${tag}) ${m.from_user_name}` : m.from_user_name;
                return (
                  <div key={m.id} className="flex items-start gap-2" data-testid={`chat-msg-${m.id}`}>
                    <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-[10px] ${hashColor(m.from_user_name)}`}>
                      {initials(m.from_user_name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-semibold text-muted-foreground mb-0.5">{fmtMsgTS(m.timestamp)}</div>
                      <div className="rounded-xl px-3 py-2" style={{ backgroundColor: "rgba(59,130,246,0.08)" }}>
                        <div className="text-sm">
                          <span className={`font-bold ${m.from_user_role === "connector" ? "text-emerald-600" : "text-primary"}`} data-testid={`chat-sender-${m.id}`}>{displayName}</span>
                          {m.kind === "doc_request" && <span className="ml-1 badge badge-yellow text-[10px]"><FileInput size={10} className="mr-0.5" /> Doc Request</span>}
                          <span className="ml-2 break-words">{m.text}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {user?.role !== "connector" && (
              <div className="px-3 py-1 text-[11px] text-red-500 font-semibold border-t border-border/40">
                This message will be sent to the connector email ID.
              </div>
            )}
            <form onSubmit={sendMsg} className="p-3 border-t border-border/60">
              <div className="flex items-start gap-2 bg-slate-50 dark:bg-slate-800/60 rounded-xl px-3 py-2">
                <textarea
                  rows={3}
                  className="flex-1 bg-transparent outline-none text-sm resize-none min-h-[70px] leading-snug"
                  placeholder="Enter message...&#10;(Enter = send, Shift+Enter = new line)"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMsg();
                    }
                  }}
                  data-testid="chat-input"
                />
                <div className="flex flex-col items-center gap-1">
                  <button type="button" className="btn btn-ghost !p-1 text-muted-foreground" title="Attach"><Paperclip size={16} /></button>
                  <button type="button" className="btn btn-ghost !p-1 text-muted-foreground" title="Emoji"><Smile size={16} /></button>
                  <button type="submit" className="w-10 h-10 rounded-lg bg-primary text-white flex items-center justify-center" title="Send" data-testid="chat-send"><Send size={16} /></button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Doc viewer modal */}
      {viewer && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeViewer}>
          <div className="rounded-2xl bg-white dark:bg-slate-900 max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <div className="font-semibold truncate">{viewer.filename}</div>
              <div className="flex gap-2">
                <button className="btn btn-outline btn-sm" onClick={() => download(viewer.doc)}><Download size={14} /> Download</button>
                <button className="btn btn-ghost btn-sm" onClick={closeViewer}><X size={14} /></button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-slate-100 dark:bg-slate-950">
              {viewer.mime?.startsWith("image") ? (
                <img src={viewer.url} alt={viewer.filename} className="max-w-full mx-auto" />
              ) : (
                <iframe src={viewer.url} title={viewer.filename} className="w-full h-[80vh]" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
